import { Link } from "react-router-dom";
import { CREATOR_PROGRAM_PATH } from "../../config/creatorProgramPaths";
import { SELLER_PROGRAM_PATH } from "../../config/sellerProgramPaths";
import {
  ArrowRightIcon,
  MegaphoneIcon,
  PaintBrushIcon,
  ShoppingBagIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

const personaCardClass =
  "group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_50px_-20px_rgba(59,59,152,0.35)] sm:rounded-3xl sm:p-6";

export function ShopperPersonaCard() {
  return (
    <Link
      to="/shop"
      className={`${personaCardClass} hover:border-violet-200`}
    >
      <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-gradient-to-br from-violet-200/50 to-transparent blur-2xl transition group-hover:from-violet-300/60" />
      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-violet-700">
          <ShoppingBagIcon className="h-3 w-3" aria-hidden />
          For shoppers
        </span>
        <p className="mt-3 text-lg font-extrabold tracking-tight text-slate-900 sm:text-xl">
          Wear what{" "}
          <span className="bg-gradient-to-r from-[#3B3B98] to-[#9B59B6] bg-clip-text text-transparent">
            no one else has
          </span>
        </p>
        <p className="mt-1.5 text-sm text-slate-500">
          Limited drops · Premium print · Morocco-made
        </p>
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#3B3B98] transition group-hover:gap-2">
          Shop drops
          <ArrowRightIcon className="h-4 w-4" aria-hidden />
        </span>
      </div>
    </Link>
  );
}

export function SellerPersonaCard() {
  return (
    <Link
      to={SELLER_PROGRAM_PATH}
      className={`${personaCardClass} hover:border-indigo-200`}
    >
      <div className="pointer-events-none absolute -left-6 -bottom-10 h-36 w-36 rounded-full bg-gradient-to-tr from-indigo-200/40 to-transparent blur-2xl transition group-hover:from-indigo-300/50" />
      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-700">
          <PaintBrushIcon className="h-3 w-3" aria-hidden />
          For sellers
        </span>
        <p className="mt-3 text-lg font-extrabold tracking-tight text-slate-900 sm:text-xl">
          Turn designs into{" "}
          <span className="bg-gradient-to-r from-[#3B3B98] to-[#9B59B6] bg-clip-text text-transparent">
            income
          </span>
        </p>
        <p className="mt-1.5 text-sm text-slate-500">
          Zero inventory · Your storefront · We fulfill
        </p>
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#3B3B98] transition group-hover:gap-2">
          Start selling
          <ArrowRightIcon className="h-4 w-4" aria-hidden />
        </span>
      </div>
    </Link>
  );
}

export function CreatorPersonaCard({ className = '' }: Readonly<{ className?: string }>) {
  return (
    <Link
      to={CREATOR_PROGRAM_PATH}
      className={`${personaCardClass} hover:border-fuchsia-200 ${className}`}
    >
      <div className="pointer-events-none absolute -right-6 -bottom-8 h-36 w-36 rounded-full bg-gradient-to-tr from-fuchsia-200/50 to-transparent blur-2xl transition group-hover:from-fuchsia-300/60" />
      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-fuchsia-700">
          <MegaphoneIcon className="h-3 w-3" aria-hidden />
          For creators
        </span>
        <p className="mt-3 text-lg font-extrabold tracking-tight text-slate-900 sm:text-xl">
          Promote drops &{" "}
          <span className="bg-gradient-to-r from-fuchsia-600 to-pink-600 bg-clip-text text-transparent">
            earn commission
          </span>
        </p>
        <p className="mt-1.5 text-sm text-slate-500">
          Referral links · Creator Studio · No inventory
        </p>
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-fuchsia-700 transition group-hover:gap-2">
          Join Creator Program
          <ArrowRightIcon className="h-4 w-4" aria-hidden />
        </span>
      </div>
    </Link>
  );
}

export function FooterPersonaCards() {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
      <ShopperPersonaCard />
      <SellerPersonaCard />
      <CreatorPersonaCard className="sm:col-span-2 lg:col-span-1" />
    </div>
  );
}

export function FooterBrandLine() {
  return (
    <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-[11px] font-medium text-slate-400 sm:text-xs">
      <SparklesIcon className="h-3.5 w-3.5 text-purple-400" aria-hidden />
      <span>Designed in Morocco</span>
      <span className="text-slate-300" aria-hidden>
        ·
      </span>
      <span>Secure checkout</span>
      <span className="text-slate-300" aria-hidden>
        ·
      </span>
      <span>Tracked worldwide</span>
    </p>
  );
}
